import { Outlet, createRootRoute } from "@tanstack/react-router";
import { Menu, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { ensureSessionRestored } from "@/auth/login";
import { useAuth } from "@/auth/stub";
import { AppSidebar } from "@/components/AppSidebar";
import { Logo } from "@/components/Logo";
import { NotificationsBell } from "@/components/NotificationsBell";
import { SearchBox } from "@/components/SearchBox";
import { ThemeToggle } from "@/components/ThemeToggle";
import { TopBar } from "@/components/TopBar";
import { UploadProgress } from "@/components/UploadProgress";
import { BrandingEffect, useAppName } from "@/lib/branding";
import { useLiveUpdates } from "@/lib/live";

/** Signed-in layout: sidebar navigation, a slim bar, the page. */
function AppShell() {
  const { t } = useTranslation();
  const appName = useAppName();
  useLiveUpdates();
  const [drawerOpen, setDrawerOpen] = useState(false);
  useEffect(() => {
    if (!drawerOpen) return;
    const close = (e: KeyboardEvent) =>
      e.key === "Escape" && setDrawerOpen(false);
    window.addEventListener("keydown", close);
    return () => window.removeEventListener("keydown", close);
  }, [drawerOpen]);

  return (
    <div className="flex min-h-screen">
      <div className="sticky top-0 hidden h-screen shrink-0 md:block">
        <AppSidebar />
      </div>
      {drawerOpen && (
        <div className="fixed inset-0 z-40 flex md:hidden">
          <div className="h-full shadow-xl">
            <AppSidebar onNavigate={() => setDrawerOpen(false)} />
          </div>
          <button
            type="button"
            className="flex-1 bg-black/40"
            aria-label={t("nav.close_menu")}
            onClick={() => setDrawerOpen(false)}
          />
        </div>
      )}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex h-12 items-center gap-2 border-b bg-background px-3 md:px-6">
          <button
            type="button"
            className="flex h-9 w-9 items-center justify-center rounded-md hover:bg-muted md:hidden"
            aria-label={drawerOpen ? t("nav.close_menu") : t("nav.open_menu")}
            aria-expanded={drawerOpen}
            onClick={() => setDrawerOpen((o) => !o)}
          >
            {drawerOpen ? (
              <X className="h-5 w-5" aria-hidden />
            ) : (
              <Menu className="h-5 w-5" aria-hidden />
            )}
          </button>
          <span className="flex items-center gap-2 font-semibold md:hidden">
            <Logo className="h-5 w-5" />
            {appName}
          </span>
          <div className="flex min-w-0 flex-1 justify-center px-2">
            <SearchBox />
          </div>
          <ThemeToggle />
          <NotificationsBell />
        </header>
        <main className="flex-1 px-3 py-5 md:px-6">
          <Outlet />
        </main>
      </div>
      <UploadProgress />
    </div>
  );
}

export const Route = createRootRoute({
  // Last-resort boundary: a rendering bug shows an error message and a
  // way back instead of blanking the whole app.
  errorComponent: function RootError({ error }: { error: unknown }) {
    const { t } = useTranslation();
    return (
      <div className="mx-auto max-w-xl py-12 text-center" role="alert">
        <h1 className="text-lg font-semibold">{t("files.error")}</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          {error instanceof Error ? error.message : String(error)}
        </p>
        <a className="mt-4 inline-block text-sm underline" href="/">
          {t("app.back_home")}
        </a>
      </div>
    );
  },
  component: function RootComponent() {
    // Restore the session once, at the app root, so public pages
    // (link access, login-flow approval) also know who is signed in.
    // Memoised: AuthGate's call reuses the same promise.
    useEffect(() => {
      void ensureSessionRestored();
    }, []);

    const signedIn = useAuth((s) => s.user !== null);
    if (signedIn)
      return (
        <>
          <BrandingEffect />
          <AppShell />
        </>
      );
    return (
      <div className="min-h-screen flex flex-col">
        <BrandingEffect />
        <TopBar />
        <main className="container mx-auto flex-1 py-6">
          <Outlet />
        </main>
        <UploadProgress />
      </div>
    );
  },
});
